import React, { useEffect, useState } from 'react';
import Navbar from '../../Navbar';
import DatePicker from "react-datepicker";
import Footer from '../../Footer';
import OutletSearch from '../../SelectSearch/OutletSearch';
import axios from 'axios';
import { formatSalesValue } from '../../../utils/formatUtils';
import VendorDetailedTable from './VendorDetailedTable';
import DepartmentDetailedTable from './DepartmentDetailedTable';
import { MdAutorenew, MdError } from 'react-icons/md';

const DetailedPurchaseReport = () => {
    const [vendorData, setVendorData] = useState([]);
    const [departmentData, setDepartmentData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [aggregated, setAggregated] = useState(false);
    const [department, setDepartment] = useState(false);

    const token = localStorage.getItem("token");
    const company = localStorage.getItem("selectedCompany");

    const handleSearch = async () => {
        if (!selectedOutlet || !token) return;

        setLoading(true);
        setError("");

        try {
            const payload = {
                token: token,
                outlet: selectedOutlet,
                dateStart: startDate.toLocaleDateString('en-CA'),
                dateEnd: endDate.toLocaleDateString('en-CA'),
                aggregated: aggregated ? "True" : "False",
                departmentwise: department ? "True" : "False",
            };

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/vendorwise-purchaseitem-lists`,
                payload
            );

            setVendorData(response.data.vendors || []);
            setDepartmentData(response.data.departments || []);
            setSummary(response.data.summary || null);
        } catch (error) {
            console.error(error);
            setError(
                error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                "Failed to fetch data");
            setVendorData([]);
            setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const handleAggregatedChange = (e) => {
        setAggregated(e.target.checked);
    };

    const handleDepartmentChange = (e) => {
        setDepartment(e.target.checked);
    };

    useEffect(() => {
        if (selectedOutlet && token) {
            handleSearch();
        }
    }, [selectedOutlet, token, department]);

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className='flex-grow py-4'>
                {/* Search Input and Select Group */}
                <div className="max-w-4xl mx-auto flex flex-col items-center justify-center mb-3 p-4 border border-gray-300 rounded-lg shadow-md">
                    <div className="flex justify-center">
                        <OutletSearch
                            selectedOutlet={selectedOutlet}
                            setSelectedOutlet={setSelectedOutlet}
                            token={token}
                        />
                    </div>

                    <div className="flex w-full justify-center gap-14 pt-3">
                        {/* Date Selection */}
                        <div className="relative flex flex-col items-center">
                            <label className="font-base font-poppins md:text-base">From:</label>
                            <DatePicker
                                selected={startDate}
                                onChange={(date) => setStartDate(date)}
                                dateFormat="yyyy-MM-dd"
                                className="w-20 md:w-24 text-xs md:text-base cursor-pointer text-center border border-gray-400 rounded-md p-1"
                                popperPlacement="bottom"
                                popperModifiers={[
                                    {
                                        name: 'offset',
                                        options: { offset: [0, 8] },
                                    },
                                    {
                                        name: 'zIndex',
                                        options: { zIndex: 9999 },
                                    },
                                ]}
                            />
                        </div>
                        <div className="relative flex flex-col items-center">
                            <label className="font-base font-poppins md:text-base">To:</label>
                            <DatePicker
                                selected={endDate}
                                onChange={(date) => setEndDate(date)}
                                dateFormat="yyyy-MM-dd"
                                className="w-20 md:w-24 text-xs md:text-base cursor-pointer text-center border border-gray-400 rounded-md p-1"
                                popperPlacement="bottom"
                                popperModifiers={[
                                    {
                                        name: 'offset',
                                        options: { offset: [0, 8] },
                                    },
                                    {
                                        name: 'zIndex',
                                        options: { zIndex: 9999 },
                                    },
                                ]}
                            />
                        </div>

                        {/* Aggregate Checkbox */}
                        <div className="relative flex flex-col items-center">
                            <label className="font-base font-poppins md:text-base">Aggregate:</label>
                            <input
                                type="checkbox"
                                checked={aggregated}
                                onChange={handleAggregatedChange}
                                className="form-checkbox"
                            />
                        </div>

                        <div className="relative flex flex-col items-center">
                            <label className="font-base font-poppins md:text-base">Department:</label>
                            <input
                                type="checkbox"
                                checked={department}
                                onChange={handleDepartmentChange}
                                className="form-checkbox"
                            />
                        </div>

                        {/* Search Button */}
                        <div className="relative flex flex-col items-center">
                            <button
                                onClick={handleSearch}
                                className="px-4 py-2 rounded-sm font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 hover:shadow-md text-sm"
                            >
                                View
                            </button>
                        </div>
                    </div>
                </div>

                <div>
                    {loading ? (
                        <div className="max-w-5xl mx-auto mt-4 rounded-md bg-blue-50 p-4">
                            <div className="flex gap-3 items-center justify-center">
                                <MdAutorenew className="h-5 w-5 text-blue-300 animate-spin" />
                                <div className="text-blue-700">
                                    Loading report...
                                </div>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="max-w-5xl mx-auto mt-4 rounded-md bg-red-50 p-4">
                            <div className="flex gap-3 justify-center items-stretch">
                                <MdError className="h-5 w-5 text-red-400" />
                                <div className="text-red-700">{error}</div>
                            </div>
                        </div>
                    ) : (
                        department ? (
                            <DepartmentDetailedTable
                                departments={departmentData}
                                summary={summary}
                                aggregated={aggregated}
                                company={company}
                                selectedOutlet={selectedOutlet}
                                startDate={startDate}
                                endDate={endDate}
                            />
                        ) : (
                            <VendorDetailedTable
                                vendorData={vendorData}
                                summary={summary}
                                aggregated={aggregated}
                                company={company}
                                selectedOutlet={selectedOutlet}
                                startDate={startDate}
                                endDate={endDate}
                                formatSalesValue={formatSalesValue}
                            />
                        )

                    )}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default DetailedPurchaseReport;